import { useQueryClient } from '@tanstack/react-query';
import Avo from 'analytics/Avo';
import type { Addresses } from 'api/types/addresses.types';
import SpinnerLoader from 'components/molecules/Loaders/spinnerLoader';
import { addressAdded, clearTimeslots, selectAddress } from 'pages/newOrder/newOrder.slice';
import { REACT_QUERY_KEYS } from 'pages/queries';
import { useAddCustomerAddress, useCheckCoverageAddress } from 'pages/queries/address.queries';
// Add the import statement for 'Careem' module
import React, { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import type { AppDispatch } from 'redux-stores/store';
import type { AddressForm } from 'schemas/address-schema';
import styled from 'styled-components';
import { useBasePath } from 'utils/router/hooks/useBasePath';
import { notifyErrorObject } from 'wrappers/reporting';

import { convertCareemAddressObj } from './convertCareemAddress';

declare const Careem: any;

interface Props {
  show: boolean;
  onClose: () => void;
  isLoading: boolean;
}

const StyledLoader = styled(SpinnerLoader)`
  z-index: 1;
  top: 35%;
`;

const UseCareemAddressSheet = (props: Props) => {
  const { t } = useTranslation();
  const basePath = useBasePath();

  const { show, onClose, isLoading } = props;
  const dispatch = useDispatch<AppDispatch>();
  const hasSheetOpened = React.useRef(false);
  const queryClient = useQueryClient();
  const selectedAddress = useRef<Addresses.CareemAddress>();

  const { mutateAsync: addAddress, isLoading: careemSheetLoading } = useAddCustomerAddress({
    onSuccess: (data) => {
      dispatch(addressAdded(data));
      Avo.addressSelected({
        addressGeofence: data.address.geofenceLabel,
        cityName: data.address.cityId ?? '',
        screen: basePath ?? 'unknown',
      });
      queryClient.setQueryData([REACT_QUERY_KEYS.addressKeys.GET_ALL_CUSTOMER_ADDRESSES], { addresses: [data.address] });
    },
    onError: () => {
      toast.dark(t('common.addressNotAddedError'), {
        autoClose: 5000,
      });
    },
  });

  const { mutate: validateAddress, isLoading: isValidatingAddress } = useCheckCoverageAddress({
    onSuccess: () => {
      addAddress(convertCareemAddressObj(selectedAddress.current!!)!! as AddressForm);
    },
    onError: () => {
      toast.dark(t('common.locationNotSupported'), {
        autoClose: 5000,
      });
    },
  });

  const startLocationSearch = useCallback(async (): Promise<void> => {
    try {
      const res = await Careem.locationPickerModule.startLocationSearch({
        title: t('common.selectAddress'),
        subtitle: '',
      });
      dispatch(clearTimeslots());
      dispatch(selectAddress(''));
      selectedAddress.current = res;
      validateAddress({ lat: res.latitude, lng: res.longitude });
    } catch (error) {
      notifyErrorObject(error);
      onClose();
    }
    onClose();
  }, [dispatch, onClose, t, validateAddress]);

  useEffect(() => {
    const fetchData = async () => {
      if (show && Careem && typeof Careem !== 'undefined' && typeof Careem.locationPickerModule !== 'undefined' && !hasSheetOpened.current) {
        hasSheetOpened.current = true;
        await startLocationSearch();
        hasSheetOpened.current = false;
      }
    };

    fetchData();
  }, [show, startLocationSearch]);

  return (isLoading || careemSheetLoading || isValidatingAddress) && <StyledLoader />;
};

export default UseCareemAddressSheet;
